import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import * as BenchlingUtil from 'BenchlingUtil';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import {BENCHLING_APPS_WEB_URL} from '../BenchlingAppDAO';
import {getAppDefinitionQuery, getAppDefinitionQueryVariables} from './__generated/getAppDefinitionQuery';
import {
  GetBenchlingAppDefinitionVersions,
  GetBenchlingAppDefinitionVersionsVariables,
} from './__generated/GetBenchlingAppDefinitionVersions';
import BenchlingAppOverview from './BenchlingAppOverview';
import BenchlingAppVersionHistory from './BenchlingAppVersionHistory';
import BenchlingAppWebhookPlayground from './BenchlingAppWebhookPlayground';

export enum BenchlingAppTab {
  OVERVIEW = 'overview',
  VERSIONS = 'versions',
  TESTING = 'testing',
}

export const APP_DEF_DETAILS_QUERY = gql`
  query getAppDefinitionQuery($appDefinitionId: StrictString!)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    benchlingAppDefinition(id: $appDefinitionId) {
      ...InaccessibleInfoGraphQL
      ... on BenchlingAppDefinitionNode {
        ...BenchlingAppDefinitionGraphQL
        oauthCredential {
          ... on InaccessibleInfoNode {
            invalidId
          }
          ... on OAuthCredentialNode {
            clientId
            clientSecrets {
              suffix
              createdAt
            }
            redirectUris
          }
        }
        webhookSetupIsAccessible
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('BenchlingAppDefinition').fragment}
`;

export const VIEW_APP_INSTALL_PAGE_BUTTON_TITLE = 'View app install page';
export const VIEW_APP_IN_WORKSPACE_BUTTON_TITLE = 'View app in the workspace';
export const NO_SHARED_VERSIONS_MESSAGE =
  'This app does not have any shared versions. At least one version must be shared with this tenant before the app can be installed.';

interface BenchlingAppProps {
  id: string;
}
export type AppDefinitionWithOAuth = ExtractByTypename<
  getAppDefinitionQuery['benchlingAppDefinition'],
  'BenchlingAppDefinitionNode'
>;

function currentTenantIsOwningTenant(appDef: {owningTenant: {isCurrentTenant: boolean}}): Boolean {
  return appDef.owningTenant.isCurrentTenant;
}

export default function BenchlingGlobalAppPage(props: BenchlingAppProps): JSX.Element {
  const appDefId = props.id;
  const [currentTab, setCurrentTab] = useState<BenchlingAppTab>(BenchlingAppTab.OVERVIEW);

  return (
    <BenchlingQuery<getAppDefinitionQuery, getAppDefinitionQueryVariables>
      query={APP_DEF_DETAILS_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{appDefinitionId: appDefId}}
    >
      {(data, {refetch}) => {
        if (
          !isInaccessible(data.benchlingAppDefinition) &&
          currentTenantIsOwningTenant(data.benchlingAppDefinition)
        ) {
          return (
            <BenchlingAppSettings
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              appDefinition={data.benchlingAppDefinition}
              refetchAppDefinition={refetch}
            />
          );
        } else {
          return (
            <div>
              <InlineBanner
                bannerType={InlineBannerType.ERROR}
                text="You do not have access to manage this app."
              />
            </div>
          );
        }
      }}
    </BenchlingQuery>
  );
}

interface BenchlingAppSettingsProps {
  currentTab: BenchlingAppTab;
  setCurrentTab: (value: BenchlingAppTab) => void;
  appDefinition: AppDefinitionWithOAuth;
  refetchAppDefinition: () => void;
}

export const BENCHLING_APP_DEFINITION_VERSIONS_QUERY = gql`
  query GetBenchlingAppDefinitionVersions($id: StrictString!) @skipAudit @engComponent(name: BENCHLING_APPS) {
    benchlingAppDefinition(id: $id) {
      ...BenchlingAppDefinitionVersionsGraphQL
    }
  }
  ${getFragmentWrapper('BenchlingAppDefinitionVersions').fragment}
`;

export function BenchlingAppSettings(props: BenchlingAppSettingsProps): JSX.Element {
  const currentVersion = props.appDefinition.latestReleaseVersion!;
  const currentInstallation = props.appDefinition.installation;
  // Name should be installed version if it exists, otherwise name of latest version
  const name = currentInstallation?.name || currentVersion.name;

  useEffect(() => {
    BenchlingUtil.setWindowTitle(`${name} - Benchling Apps`);
  }, [name]);

  function makeToolbarLinkButton(): JSX.Element {
    const hasInstallation = currentInstallation != null;
    const linkUrl = hasInstallation ? currentInstallation.url : '';
    const tooltipText = hasInstallation
      ? null
      : 'No version of this app has been installed yet on this tenant.';
    const button = (
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        disabled={!hasInstallation}
        size={ButtonSize.SMALL}
        text={VIEW_APP_IN_WORKSPACE_BUTTON_TITLE}
        onClick={() => {
          navigateToURL(linkUrl);
        }}
      />
    );
    if (!tooltipText) {
      return button;
    }
    return <Tooltip text={tooltipText}>{button}</Tooltip>;
  }

  return (
    <SettingsPageContainer
      onClickTitle={() => {
        navigateToURL(BENCHLING_APPS_WEB_URL);
      }}
      subtitle={name}
      title="Apps"
      toolbar={makeToolbarLinkButton()}
    >
      <TabContainer
        activeTab={props.currentTab}
        className={classNames(css(styles.maxWidth), 'u-flex-grow')}
        onRequestTabChange={props.setCurrentTab}
      >
        <Tab tabKey={BenchlingAppTab.OVERVIEW} title="Overview">
          <BenchlingAppOverview
            appDefinition={props.appDefinition}
            refetchAppDefinition={props.refetchAppDefinition}
          />
        </Tab>
        <Tab tabKey={BenchlingAppTab.VERSIONS} title="Version History">
          <BenchlingQuery<GetBenchlingAppDefinitionVersions, GetBenchlingAppDefinitionVersionsVariables>
            query={BENCHLING_APP_DEFINITION_VERSIONS_QUERY}
            variables={{id: props.appDefinition.id}}
            renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
          >
            {(data, {refetch}) => {
              if (
                !isInaccessible(data.benchlingAppDefinition) &&
                // Not sure why TS isn't picking up that `data.benchlingAppDefinition` has been narrowed down
                // to the BenchlingAppDefinitionNode.
                currentTenantIsOwningTenant(data.benchlingAppDefinition)
              ) {
                return (
                  <BenchlingAppVersionHistory
                    appDefinition={props.appDefinition}
                    appDefinitionVersions={data.benchlingAppDefinition.versions}
                    refetchAppDefinition={() => {
                      refetch().then(() => {
                        props.refetchAppDefinition();
                      });
                    }}
                  />
                );
              } else {
                return (
                  <div>
                    <InlineBanner
                      bannerType={InlineBannerType.ERROR}
                      text="You do not have access to manage this app."
                    />
                  </div>
                );
              }
            }}
          </BenchlingQuery>
        </Tab>
        <Tab tabKey={BenchlingAppTab.TESTING} title="Webhook Testing">
          <BenchlingAppWebhookPlayground appDefinition={props.appDefinition} />
        </Tab>
      </TabContainer>
    </SettingsPageContainer>
  );
}

const styles = StyleSheet.create({
  benchlingAppMetadataField: {
    marginBottom: 15,
  },
  maxWidth: {
    maxWidth: '70em',
  },
});
